import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Globe, Plus, Loader2, CheckCircle, AlertCircle, Clock, RefreshCw, Star, Trash2, Palette, ChevronDown, ChevronRight, Save, Eraser,
} from 'lucide-react';
import { adminDomainsAPI } from '../../services/api';
import Card from '../../components/admin/Card';
import CopyField from '../../components/admin/CopyField';
import Why from '../../components/admin/Why';
import Toggle from '../../components/admin/Toggle';
import { useModuleEntitled } from '../../context/featureFlagStore';
import { NotInPlanNotice } from '../../components/common/NotInPlan';

/**
 * Admin → Domains. The portal on the MSP's own hostname (Phase 5a), and a
 * brand per hostname on top of it (5g).
 *
 * One card per hostname, each self-contained. The life of a hostname is:
 * add it → publish the records the card shows → press Verify (the card
 * polls on its own while the records are pending) → it is served. Nothing
 * on this page can mark a hostname verified; only Cloudflare's answer does,
 * which is why the verify button exists at all and why a freshly added
 * hostname is shown as "waiting on DNS" rather than "ready".
 */

const DOMAINS_KEY = ['admin-domains'];
const POLL_MS = 20 * 1000;

const inputClass =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60 dark:border-neutral-600 dark:bg-neutral-900 dark:text-white';
const buttonClass =
  'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60';
const primaryButton = `${buttonClass} bg-primary-600 text-white hover:bg-primary-700`;
const secondaryButton = `${buttonClass} border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700`;
const dangerButton = `${buttonClass} border border-red-300 bg-white text-red-700 hover:bg-red-50 dark:border-red-800 dark:bg-neutral-800 dark:text-red-400 dark:hover:bg-red-900/20`;

const errorOf = (e, fallback) => e?.response?.data?.error || e?.message || fallback;

function Notice({ tone = 'info', children }) {
  const tones = {
    info: 'border-neutral-200 bg-neutral-50 text-neutral-700 dark:border-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300',
    warn: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
    error: 'border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200',
    ok: 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-200',
  };
  return <div className={`rounded-lg border p-3 text-sm ${tones[tone]}`}>{children}</div>;
}

function StatusChip({ domain }) {
  if (domain.verified) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
        <CheckCircle className="h-3.5 w-3.5" /> Live
      </span>
    );
  }
  if (domain.errors?.length || ['moved', 'deleted', 'blocked'].includes(domain.status)) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400" title={domain.errors?.[0] || domain.status}>
        <AlertCircle className="h-3.5 w-3.5" /> Needs attention
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
      <Clock className="h-3.5 w-3.5" /> Waiting on DNS
    </span>
  );
}

/** Cloudflare's own words, with a plain-English gloss beside them. */
function statusLine(domain) {
  const ssl = domain.sslStatus || 'not requested';
  const gloss = {
    pending_validation: 'waiting for the TXT records',
    pending_issuance: 'records seen; certificate being issued',
    pending_deployment: 'certificate issued; being deployed',
    active: 'certificate active',
  }[ssl];
  return `Hostname: ${domain.status} · Certificate: ${ssl}${gloss ? ` (${gloss})` : ''}`;
}

// ---------------------------------------------------------------------------
// Records to publish
// ---------------------------------------------------------------------------

function RecordRow({ record }) {
  const label = { ownership: 'Ownership check', certificate: 'Certificate check', traffic: 'Traffic' }[record.purpose] || record.purpose;
  return (
    <div className="grid gap-2 rounded-lg border border-neutral-200 p-3 text-xs dark:border-neutral-700 md:grid-cols-[8rem_4rem_1fr_1fr]">
      <div className="font-medium text-neutral-700 dark:text-neutral-300">{label}</div>
      <div className="font-mono text-neutral-500">{record.type}</div>
      <div>
        <div className="mb-1 text-neutral-500">Name</div>
        <CopyField value={record.name} />
      </div>
      <div>
        <div className="mb-1 text-neutral-500">Value</div>
        <CopyField value={record.value} />
      </div>
    </div>
  );
}

function Records({ domain }) {
  if (!domain.records?.length) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">Cloudflare has not issued records for this hostname yet. Press Verify to ask again.</p>;
  }
  const pending = domain.records.filter((r) => r.purpose !== 'traffic');
  const traffic = domain.records.filter((r) => r.purpose === 'traffic');
  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Add these records at your DNS provider. The two TXT records prove you own the name and let the certificate be issued
        <strong> before</strong> any traffic moves; the CNAME is what finally sends visitors here, so add it last.
      </p>
      {pending.map((r) => <RecordRow key={`${r.purpose}-${r.name}`} record={r} />)}
      {traffic.map((r) => <RecordRow key={`${r.purpose}-${r.name}`} record={r} />)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-hostname branding (5g)
// ---------------------------------------------------------------------------

const BRANDING_FIELDS = [
  { name: 'portalName', label: 'Portal name', help: 'What this hostname calls the portal.' },
  { name: 'title', label: 'Browser title', help: 'Defaults to "<portal name> Customer Portal".' },
  { name: 'companyLogoUrl', label: 'Logo URL', help: 'https:// only.' },
  { name: 'loginLogoUrl', label: 'Login logo URL', help: 'Defaults to the logo.' },
  { name: 'faviconUrl', label: 'Favicon URL' },
  { name: 'loginBackgroundUrl', label: 'Login background URL' },
  { name: 'primaryColor', label: 'Primary colour', help: 'A hex colour such as #1e40af.' },
  { name: 'supportEmail', label: 'Support email' },
  { name: 'supportPhone', label: 'Support phone' },
  { name: 'companyWebsite', label: 'Website' },
  { name: 'locale', label: 'Locale', help: 'A BCP-47 tag such as en-AU.' },
  { name: 'currency', label: 'Currency', help: 'An ISO-4217 code such as AUD.' },
];

function BrandingEditor({ domain, onSaved }) {
  const [open, setOpen] = useState(Boolean(domain.branding));
  const [values, setValues] = useState(() => ({ ...(domain.branding || {}) }));
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!dirty) setValues({ ...(domain.branding || {}) });
  }, [domain.branding, dirty]);

  const save = useMutation({
    mutationFn: (branding) => adminDomainsAPI.saveBranding(domain.id, branding),
    onSuccess: (res) => {
      setDirty(false);
      setMessage({ tone: 'ok', text: res.data?.domain?.branding ? 'Saved. Visitors on this hostname now see this brand.' : 'Cleared. This hostname uses your Portal Settings.' });
      onSaved();
    },
    onError: (e) => {
      const errors = e?.response?.data?.errors;
      setMessage({ tone: 'error', text: errors?.length ? errors.map((x) => x.message).join(' ') : errorOf(e, 'Could not save branding') });
    },
  });

  const set = (name, value) => {
    setDirty(true);
    setMessage(null);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const overridden = Object.keys(domain.branding || {}).length;

  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between px-4 py-3 text-left">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
          <Palette className="h-4 w-4 text-neutral-400" /> Brand for this hostname
          {overridden > 0 && <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">{overridden} override{overridden === 1 ? '' : 's'}</span>}
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-neutral-400" /> : <ChevronRight className="h-4 w-4 text-neutral-400" />}
      </button>
      {open && (
        <div className="space-y-4 border-t border-neutral-200 p-4 dark:border-neutral-700">
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Leave a field blank to use your Portal Settings. Only visitors arriving on <strong>{domain.hostname}</strong> see these; your other hostnames are unchanged.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {BRANDING_FIELDS.map((f) => (
              <div key={f.name}>
                <label htmlFor={`${domain.id}-${f.name}`} className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">{f.label}</label>
                <input id={`${domain.id}-${f.name}`} type="text" className={inputClass} value={values[f.name] ?? ''} onChange={(e) => set(f.name, e.target.value)} autoComplete="off" placeholder="Inherit" />
                {f.help && <p className="mt-1 text-xs text-neutral-500">{f.help}</p>}
              </div>
            ))}
            <div className="flex items-center justify-between rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700">
              <div>
                <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Show "Powered by"</div>
                <p className="text-xs text-neutral-500">Unset follows Portal Settings.</p>
              </div>
              <div className="flex items-center gap-3">
                {values.poweredBy !== undefined && (
                  <button type="button" className="text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200" onClick={() => set('poweredBy', undefined)}>Inherit</button>
                )}
                <Toggle checked={values.poweredBy === true} onChange={(v) => set('poweredBy', v)} label="Show powered by" />
              </div>
            </div>
          </div>
          {message && <Notice tone={message.tone}>{message.text}</Notice>}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={primaryButton}
              disabled={!dirty || save.isPending}
              onClick={() => {
                const body = {};
                for (const [k, v] of Object.entries(values)) if (v !== undefined && v !== '') body[k] = v;
                save.mutate(Object.keys(body).length ? body : null);
              }}
            >
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save brand
            </button>
            {overridden > 0 && (
              <button type="button" className={secondaryButton} disabled={save.isPending} onClick={() => { setValues({}); setDirty(true); save.mutate(null); }}>
                <Eraser className="h-4 w-4" /> Use Portal Settings
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One hostname
// ---------------------------------------------------------------------------

function DomainCard({ domain, onChanged }) {
  const [message, setMessage] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const pollRef = useRef(null);
  // The interval's callback is created once, so it must read a ref: the
  // `verify.isPending` it captured was the first render's, always false.
  const verifying = useRef(false);

  const verify = useMutation({
    mutationFn: () => adminDomainsAPI.verify(domain.id),
    onMutate: () => { verifying.current = true; },
    onSettled: () => { verifying.current = false; },
    onSuccess: (res) => {
      const d = res.data?.domain;
      if (d?.verified && !domain.verified) setMessage({ tone: 'ok', text: `${d.hostname} is live. Your portal is now served there.` });
      else if (d?.errors?.length) setMessage({ tone: 'warn', text: d.errors.join(' ') });
      else setMessage(null);
      onChanged();
    },
    onError: (e) => setMessage({ tone: 'error', text: errorOf(e, 'Could not verify') }),
  });

  const makePrimary = useMutation({
    mutationFn: () => adminDomainsAPI.setPrimary(domain.id),
    onSuccess: () => { setMessage({ tone: 'ok', text: 'Links in your emails now point here.' }); onChanged(); },
    onError: (e) => setMessage({ tone: 'error', text: errorOf(e, 'Could not set the primary domain') }),
  });

  const remove = useMutation({
    mutationFn: () => adminDomainsAPI.remove(domain.id),
    onSuccess: onChanged,
    onError: (e) => { setConfirmRemove(false); setMessage({ tone: 'error', text: errorOf(e, 'Could not remove') }); },
  });

  // A hostname waiting on DNS is re-checked on a timer while this card is
  // on screen, so the admin can add the records and watch it go live
  // without pressing anything. Verified ones are not polled; a verify is a
  // Cloudflare read and there is nothing to learn from repeating it.
  const shouldPoll = !domain.verified && !['moved', 'deleted', 'blocked'].includes(domain.status);
  useEffect(() => {
    if (!shouldPoll) return undefined;
    pollRef.current = setInterval(() => { if (!verifying.current) verify.mutate(); }, POLL_MS);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldPoll, domain.id]);

  const aside = (
    <div className="flex items-center gap-3">
      {domain.isPrimary && (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-800 dark:bg-primary-900/40 dark:text-primary-200">
          <Star className="h-3 w-3" /> Primary
        </span>
      )}
      <StatusChip domain={domain} />
    </div>
  );

  return (
    <Card icon={Globe} title={domain.hostname} description={statusLine(domain)} aside={aside}>
      <div className="space-y-4">
        {domain.verified ? (
          <Notice tone="ok">
            Live. Visitors to <strong>https://{domain.hostname}</strong> reach this portal with a certificate Cloudflare issues and renews.
            {!domain.isPrimary && ' Make it primary to use it in the links your emails send.'}
          </Notice>
        ) : (
          <Records domain={domain} />
        )}

        {domain.checkedAt && (
          <p className="text-xs text-neutral-500">Last checked {new Date(domain.checkedAt).toLocaleString()}.</p>
        )}
        {message && <Notice tone={message.tone}>{message.text}</Notice>}

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className={domain.verified ? secondaryButton : primaryButton} disabled={verify.isPending} onClick={() => verify.mutate()}>
            {verify.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {domain.verified ? 'Re-check' : 'Verify'}
          </button>
          {domain.verified && !domain.isPrimary && (
            <button type="button" className={secondaryButton} disabled={makePrimary.isPending} onClick={() => makePrimary.mutate()}>
              {makePrimary.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Make primary
            </button>
          )}
          <span className="flex-1" />
          {confirmRemove ? (
            <>
              <span className="text-sm text-neutral-600 dark:text-neutral-300">Remove {domain.hostname}? Visitors there will get an error until you remove the CNAME.</span>
              <button type="button" className={dangerButton} disabled={remove.isPending} onClick={() => remove.mutate()}>
                {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove
              </button>
              <button type="button" className={secondaryButton} onClick={() => setConfirmRemove(false)}>Keep</button>
            </>
          ) : (
            <button type="button" className={dangerButton} onClick={() => setConfirmRemove(true)}>
              <Trash2 className="h-4 w-4" /> Remove
            </button>
          )}
        </div>

        {domain.verified && <BrandingEditor domain={domain} onSaved={onChanged} />}

        <Why>
          <p><strong>Why two TXT records before the CNAME?</strong> Cloudflare proves you own the name and issues the certificate from the TXT records alone. If the CNAME went first, visitors would see a certificate error until issuance finished.</p>
          <p><strong>Verify says "pending_validation" after I added the records.</strong> DNS changes take a few minutes to be visible; this card re-checks every twenty seconds while it is open. Check the record names exactly — the ownership record starts with <code>_cf-custom-hostname.</code></p>
          <p><strong>"moved" or "blocked".</strong> Cloudflare has stopped serving this name — the CNAME now points elsewhere, or the name was claimed on another zone. The hostname is no longer served here until that is resolved and Verify reports it active again.</p>
        </Why>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export default function DomainsPage() {
  const queryClient = useQueryClient();
  const [hostname, setHostname] = useState('');
  const [addError, setAddError] = useState(null);
  // A custom domain is the White label add-on (Phase 7a). Without it the
  // list and Remove still work; adding and verifying are refused (403).
  const whitelabel = useModuleEntitled('whitelabel');

  const { data, error, isLoading } = useQuery({
    queryKey: DOMAINS_KEY,
    queryFn: () => adminDomainsAPI.list().then((r) => r.data),
    retry: false,
  });

  const onChanged = () => queryClient.invalidateQueries({ queryKey: DOMAINS_KEY });

  const add = useMutation({
    mutationFn: (h) => adminDomainsAPI.add(h),
    onSuccess: () => { setHostname(''); setAddError(null); onChanged(); },
    onError: (e) => setAddError(errorOf(e, 'Could not add the hostname')),
  });

  const domains = useMemo(() => data?.domains || [], [data]);
  const custom = domains.filter((d) => d.kind === 'custom');
  const forbidden = error?.response?.status === 403;
  const noTenant = error?.response?.status === 409;

  return (
    <div className="mx-auto max-w-5xl space-y-8">
      <div>
        <div className="mb-2 flex items-center gap-3">
          <Globe className="h-6 w-6 text-neutral-400" />
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Domains</h1>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">
          Serve this portal on your own domain. You add one CNAME; the certificate is issued and renewed for you.
        </p>
      </div>

      {forbidden || noTenant ? (
        <Notice tone="warn">{error.response.data?.error || 'You do not have the settings permission.'}</Notice>
      ) : error ? (
        <Notice tone="error">{errorOf(error, 'Could not load domains')}</Notice>
      ) : (
        <>
          <Card icon={Plus} title="Add a hostname" description="A name you own, such as portal.acme.com. Nothing is served there until it is verified.">
            {data && !data.enabled && (
              <div className="mb-4">
                <Notice tone="warn">Custom domains are not enabled on this platform yet. Your portal is still reachable at its own address.</Notice>
              </div>
            )}
            {!whitelabel && (
              <div className="mb-4">
                <NotInPlanNotice />
              </div>
            )}
            <form
              className="flex flex-col gap-2 md:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (hostname.trim() && whitelabel) add.mutate(hostname.trim());
              }}
            >
              <input
                type="text"
                className={inputClass}
                placeholder="portal.acme.com"
                value={hostname}
                onChange={(e) => { setHostname(e.target.value); setAddError(null); }}
                disabled={add.isPending || (data && !data.enabled) || !whitelabel}
                autoComplete="off"
                spellCheck={false}
              />
              <button
                type="submit"
                className={primaryButton}
                disabled={!hostname.trim() || add.isPending || (data && !data.enabled) || !whitelabel}
                title={whitelabel ? undefined : 'Not included in your plan'}
              >
                {add.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
              </button>
            </form>
            {addError && <div className="mt-3"><Notice tone="error">{addError}</Notice></div>}
            {data?.cnameTarget && (
              <p className="mt-3 text-xs text-neutral-500 dark:text-neutral-400">
                Hostnames you add here will CNAME to <code className="rounded bg-neutral-100 px-1 dark:bg-neutral-900">{data.cnameTarget}</code>.
              </p>
            )}
          </Card>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : custom.length === 0 ? (
            <Notice tone="info">No custom hostnames yet. Your portal is served at its platform address.</Notice>
          ) : (
            custom.map((d) => <DomainCard key={d.id} domain={d} onChanged={onChanged} />)
          )}
        </>
      )}
    </div>
  );
}
